import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BackendService } from './backend.service';

export interface SessionSummary {
  session_id: string;
  title: string;
  summary_preview?: string;
  active_agent_id: string | null;
  state: 'HOME' | 'IN_AGENT_MODE';
  updated_at: number;
  tags?: string[];
}

export interface SessionListResponse {
  sessions: SessionSummary[];
  next_cursor: string | null;
  degraded: boolean;
}

// The REST router wraps every response in {success, message, body}
// (see wg-agents-api/src/router/rest/app.py::_ok). Map to the inner body.
interface ApiEnvelope<T> {
  success: boolean;
  message: string;
  body: T | null;
}

const EMPTY_LIST: SessionListResponse = { sessions: [], next_cursor: null, degraded: true };

@Injectable({ providedIn: 'root' })
export class SessionListService {
  constructor(
    private readonly http: HttpClient,
    private readonly backend: BackendService,
  ) {}

  getSessions(limit = 50, cursor?: string): Observable<SessionListResponse> {
    let path = `agenthome/sessions?limit=${limit}`;
    if (cursor) path += `&cursor=${encodeURIComponent(cursor)}`;
    const url = this.backend.createAgentsUrl(path);
    return this.http.get<ApiEnvelope<SessionListResponse>>(url).pipe(
      map(res => {
        const body = res?.body;
        if (!body) return EMPTY_LIST;
        return {
          sessions: body.sessions ?? [],
          next_cursor: body.next_cursor ?? null,
          degraded: body.degraded ?? false,
        };
      }),
      catchError(() => of(EMPTY_LIST)),
    );
  }

  getSession(sessionId: string): Observable<SessionSummary | null> {
    const url = this.backend.createAgentsUrl(`agenthome/sessions/${encodeURIComponent(sessionId)}`);
    return this.http.get<ApiEnvelope<SessionSummary>>(url).pipe(
      map(res => res?.body ?? null),
      catchError(() => of(null)),
    );
  }

  deleteSession(sessionId: string): Observable<boolean> {
    const url = this.backend.createAgentsUrl(`agenthome/sessions/${encodeURIComponent(sessionId)}`);
    return this.http.delete<ApiEnvelope<null>>(url).pipe(
      map(res => res?.success ?? false),
      catchError(() => of(false)),
    );
  }

  /** Rename a session. Returns the saved (server-trimmed) title, or null on failure. */
  renameSession(sessionId: string, title: string): Observable<string | null> {
    const url = this.backend.createAgentsUrl(`agenthome/sessions/${encodeURIComponent(sessionId)}`);
    return this.http.patch<ApiEnvelope<{ session_id: string; title: string }>>(url, { title }).pipe(
      map(res => (res?.success ? res.body?.title ?? title : null)),
      catchError(() => of(null)),
    );
  }
}
